import dayjs from 'dayjs';
import { IntegrationType } from '../../../../api/integrations-client/integrations-client.type';

/**
 * Returns the display name for a given data source type
 *
 * @param {IntegrationType | null} type - The integration type (JIRA, ADO, JDC, GITHUB, LINEAR) or null for all sources
 * @returns {string} The human-readable name of the data source
 */

function getDataSourceName(type: IntegrationType | null): string {
  switch (type) {
    case null:
      return 'All Data Sources';
    case IntegrationType.JIRA:
      return 'Atlassian Jira Software Cloud';
    case IntegrationType.ADO:
      return 'Microsoft Azure DevOps';
    case IntegrationType.JDC:
      return 'Jira Data Center';
    case IntegrationType.GITHUB:
      return 'Github';
    case IntegrationType.LINEAR:
      return 'Linear';
    default:
      return 'Unknown Data Source';
  }
}

/**
 * Checks if the data source is up to date based on the last refresh time
 *
 * @param {string | null} lastRefresh - The last refresh time
 * @returns {boolean} - True if the data source is up to date, false otherwise
 */
function isUpToDate(lastRefresh: string | null): boolean {
  // December 21, 2012 is the backend's default placeholder indicating data was never ingested
  // See: healthapi/apps/base_model.py - last_data_fetch default=datetime(2012, 12, 21, 23, 59)
  if (lastRefresh === dayjs('2012-12-21').format('YYYY-MM-DD')) {
    return false;
  }

  if (!lastRefresh) return false;
  const lastRefreshDate = new Date(lastRefresh);
  const currentDate = new Date();
  const twentyFourHoursInMs = 24 * 60 * 60 * 1000;
  return currentDate.getTime() - lastRefreshDate.getTime() <= twentyFourHoursInMs;
}

/**
 * Checks if an project or board is considered active based on its last refresh time
 *
 * @param {string | null} lastRefresh - The last refresh time
 * @returns {boolean} - True if the object was refreshed within the last 30 days, false otherwise
 */
function isProjectOrBoardActive(lastRefresh: string | null): boolean {
  if (!lastRefresh) return false;
  const lastRefreshDate = new Date(lastRefresh);
  const currentDate = new Date();
  const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;
  return currentDate.getTime() - lastRefreshDate.getTime() <= thirtyDaysInMs;
}

/**
 * Checks if the datetime represents the "never ingested" placeholder date (December 21, 2012)
 * See: healthapi/apps/base_model.py - last_data_fetch default=datetime(2012, 12, 21, 23, 59)
 *
 * @param {string | null | undefined} datetime - The datetime string to check
 * @returns {boolean} - True if the datetime is the placeholder date, false otherwise
 */
function isNeverIngestedDate(datetime: string | null | undefined): boolean {
  if (!datetime) return true;
  const parsed = dayjs(datetime);
  return parsed.year() === 2012 && parsed.month() === 11 && parsed.date() === 21;
}

export { getDataSourceName, isNeverIngestedDate, isProjectOrBoardActive, isUpToDate };
